import { useIsMutating } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Outlet } from 'react-router';

import { Footer } from '@/components/Footer/Footer.tsx';
import { Header } from '@/components/Header/Header.tsx';
import { MobileMenu } from '@/components/MobileMenu/MobileMenu.tsx';
import { DeleteProfileContent } from '@/components/shared/Modal/content/delete-profile/DeleteProfileContent.tsx';
import { ErrorContent } from '@/components/shared/Modal/content/error/ErrorContent.tsx';
import { InviteFriendContent } from '@/components/shared/Modal/content/invite-friend/InviteFriendContent.tsx';
import { InviteSuccessContent } from '@/components/shared/Modal/content/invite-success/InviteSuccessContent.tsx';
import { NumberReservationContent } from '@/components/shared/Modal/content/number-reservation/NumberReservationContent.tsx';
import { ErrorPurchaseContent } from '@/components/shared/Modal/content/purchase-number/error-purchase/ErrorPurchaseContent.tsx';
import { PurchaseNumberContent } from '@/components/shared/Modal/content/purchase-number/PurchaseNumberContent.tsx';
import { SuccessfulPurchaseContent } from '@/components/shared/Modal/content/purchase-number/successful-purchase/SuccessfulPurchaseContent.tsx';
import { ReservationCancelContent } from '@/components/shared/Modal/content/reservation-cancel/ReservationCancelContent.tsx';
import { ModalSignInContent } from '@/components/shared/Modal/content/sign-in/ModalSignInContent.tsx';
import { ModalSignUpContent } from '@/components/shared/Modal/content/sign-up/ModalSignUpContent.tsx';
import { WithdrawalContent } from '@/components/shared/Modal/content/withdrawal/WithdrawalContent.tsx';
import { ModalBase } from '@/components/shared/Modal/ModalBase.tsx';
import { useAndroidBackEvent } from '@/hooks';
import { NoConnectionPage } from '@/pages/no-connection/NoConnectionPage.tsx';
import { useAuthStore, useChosenNumberStore, useFilterStore, useModalStore } from '@/store';
import { useErrorStore } from '@/store/useErrorStore.ts';

import cn from './styles.module.sass';

export const MainLayout = () => {
    const { isFiltersOpen } = useFilterStore();
    const { isOnline } = useErrorStore();
    const { isBtnDisabled } = useChosenNumberStore();
    const { isAuth } = useAuthStore();
    const {
        isOpen,
        isAuthHidden,
        setIsOpen,
        showSignUp,
        setShowSignUp,
        isWithdrawalOpen,
        setIsWithdrawalOpen,
        isInviteOpen,
        setIsInviteOpen,
        isCancelReservationOpen,
        setIsCancelReservationOpen,
        isReservationOpen,
        setIsReservationOpen,
        isErrorOpen,
        setIsErrorOpen,
        isProfileDeletionOpen,
        setIsProfileDeletionOpen,
        isInviteSuccessOpen,
        setIsInviteSuccessOpen,
        isPurchaseSuccessOpen,
        setIsPurchaseSuccessOpen,
        isPurchaseOpen,
        setIsPurchaseOpen,
        isPurchaseErrorOpen,
        setIsPurchaseErrorOpen,
        hasOpenModals,
        closeAllModals
    } = useModalStore();

    const isMutatingReserveNumber = useIsMutating({ mutationKey: ['reserve-number'] });

    const handleCloseReservation = () => {
        if (isMutatingReserveNumber === 0) {
            setIsReservationOpen(false);
        }
    };

    const handleOpenFromMenu = (signingUp: boolean) => {
        setIsOpen(true);
        setShowSignUp(signingUp);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    /*
     Обрабатываем эвент "назад" в мобильном
     андройд приложении
     */
    useAndroidBackEvent(() => {
        if (hasOpenModals()) {
            closeAllModals();
            return;
        }

        history.back();
    });

    return (
        <>
            <Header />
            <div
                style={isFiltersOpen ? { marginBottom: 0 } : undefined}
                className={clsx('app-container', !isBtnDisabled && cn.itemChosen)}
            >
                {isOnline ? <Outlet /> : <NoConnectionPage />}
            </div>
            <Footer />
            {!isFiltersOpen && <MobileMenu />}
            <ModalBase isOpen={!isAuth && isOpen} isHidden={isAuthHidden} isNotAuth={false} onClose={handleClose}>
                {showSignUp ? (
                    <ModalSignUpContent openSignIn={handleOpenFromMenu} />
                ) : (
                    <ModalSignInContent handleClose={handleClose} openSignUp={handleOpenFromMenu} />
                )}
            </ModalBase>

            <ModalBase isOpen={isWithdrawalOpen} onClose={() => setIsWithdrawalOpen(false)}>
                <WithdrawalContent />
            </ModalBase>

            <ModalBase isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)}>
                <InviteFriendContent />
            </ModalBase>

            <ModalBase
                isOpen={isCancelReservationOpen}
                onClose={() => setIsCancelReservationOpen(false)}
            >
                <ReservationCancelContent />
            </ModalBase>

            <ModalBase isOpen={isReservationOpen} onClose={handleCloseReservation}>
                <NumberReservationContent />
            </ModalBase>

            <ModalBase isOpen={isErrorOpen} onClose={() => setIsErrorOpen(false)}>
                <ErrorContent />
            </ModalBase>

            <ModalBase
                isOpen={isProfileDeletionOpen}
                onClose={() => setIsProfileDeletionOpen(false)}
            >
                <DeleteProfileContent />
            </ModalBase>

            <ModalBase isOpen={isInviteSuccessOpen} onClose={() => setIsInviteSuccessOpen(false)}>
                <InviteSuccessContent />
            </ModalBase>

            <ModalBase
                isOpen={isPurchaseSuccessOpen}
                onClose={() => {
                    setIsPurchaseSuccessOpen(false);
                    localStorage.removeItem('preliminaryReservedMsisdn');
                }}
            >
                <SuccessfulPurchaseContent />
            </ModalBase>

            <ModalBase isOpen={isPurchaseOpen} onClose={() => setIsPurchaseOpen(false)}>
                <PurchaseNumberContent />
            </ModalBase>

            <ModalBase isOpen={isPurchaseErrorOpen} onClose={() => setIsPurchaseErrorOpen(false)}>
                <ErrorPurchaseContent />
            </ModalBase>
        </>
    );
};
